import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useModal } from '../components/ModalProvider';
import ProfileModal from '../components/ProfileModal';
import ThemeToggle from '../components/ThemeToggle';
import AccentPicker from '../components/AccentPicker';
import Avatar from '../components/Avatar';
import { NAV_GROUPS, canSeeItem } from '../menuConfig';
import { roleName } from '../utils/format';
import { APP_VERSION } from '../version';

// Trang "Menu" thay sidebar trên mobile: hồ sơ, tìm chức năng, các nhóm chức năng (mở/gập),
// tiện ích, cài đặt giao diện, đăng xuất. Menu ĐỘNG theo vai trò (canSeeItem).
// Icon nhóm + màu nền khớp thiết kế.
const GROUP_META = {
  'KINH DOANH': { ico: 'ti-briefcase', tint: '#4b47e6', label: 'Kinh doanh' },
  'NHÂN SỰ & KPI': { ico: 'ti-users-group', tint: '#0d9488', label: 'Nhân sự & KPI' },
  'BÁO CÁO': { ico: 'ti-chart-histogram', tint: '#d97706', label: 'Báo cáo' },
  'NỘI BỘ': { ico: 'ti-message-2', tint: '#2563eb', label: 'Nội bộ' },
  'GIẢI TRÍ': { ico: 'ti-dice', tint: '#7c3aed', label: 'Giải trí' },
  'HỆ THỐNG': { ico: 'ti-refresh', tint: '#6b7078', label: 'Hệ thống' },
};
// Nhóm gộp xuống mục "Tiện ích" cho gọn (khớp thiết kế: Thông báo/Ghi chú/Hướng dẫn tách riêng).
const UTIL_KEYS = ['announcements', 'notes', 'guide'];

export default function MobileMenuPage({ setPage }) {
  const { me, logout } = useAuth();
  const { openModal, closeModal } = useModal();
  const [q, setQ] = useState('');
  const [openG, setOpenG] = useState('');

  const go = (key) => setPage(key);
  const kw = q.trim().toLowerCase();

  // Khi tìm: bung phẳng mọi mục khớp (bất kể nhóm). Không tìm: hiện nhóm gập.
  const allItems = NAV_GROUPS.flatMap((g) => g.items).filter((i) => canSeeItem(me, i));
  const utilItems = allItems.filter((i) => UTIL_KEYS.includes(i.key));
  const searchHits = kw ? allItems.filter((i) => i.label.toLowerCase().includes(kw)) : [];

  return (
    <div className="mbmenu">
      {/* Hồ sơ */}
      <button type="button" className="mbmenu-profile card" onClick={() => openModal(<ProfileModal onClose={closeModal} />)}>
        <Avatar name={me.name} src={me.avatar} size={48} />
        <div className="mbmenu-profile-main">
          <div className="mbmenu-profile-name">{me.name}</div>
          <div className="mbmenu-profile-role">{roleName(me.displayRole || me.role)}{me.group ? ` · ${me.group.name}` : ''}</div>
        </div>
        <i className="ti ti-chevron-right mbmenu-chev" />
      </button>

      {/* Tìm chức năng */}
      <div className="mbmenu-search">
        <i className="ti ti-search" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Tìm chức năng…" />
      </div>

      {kw ? (
        <div className="mbmenu-sec">
          {searchHits.length ? searchHits.map((i) => (
            <button key={i.key} type="button" className="mbmenu-row" onClick={() => go(i.key)}>
              <span className="mbmenu-row-ico"><i className={`ti ${i.ico}`} /></span>
              <span className="mbmenu-row-label">{i.label}</span>
              <i className="ti ti-chevron-right mbmenu-chev" />
            </button>
          )) : <div className="empty">Không tìm thấy chức năng.</div>}
        </div>
      ) : (
        <>
          {/* Các nhóm chức năng (gập/mở) — bỏ nhóm TỔNG QUAN vì đã ở bottom-nav; bỏ mục Tiện ích khỏi nhóm NỘI BỘ */}
          <div className="mbmenu-sec">
            {NAV_GROUPS.filter((g) => g.title !== 'TỔNG QUAN').map((g) => {
              const items = g.items.filter((i) => canSeeItem(me, i) && !UTIL_KEYS.includes(i.key));
              if (!items.length) return null;
              const meta = GROUP_META[g.title] || { ico: 'ti-folder', tint: '#6b7078' };
              const open = openG === g.title;
              return (
                <div key={g.title}>
                  <button type="button" className="mbmenu-row" onClick={() => setOpenG(open ? '' : g.title)}>
                    <span className="mbmenu-row-ico" style={{ color: meta.tint, background: `color-mix(in srgb, ${meta.tint} 12%, transparent)` }}>
                      <i className={`ti ${meta.ico}`} />
                    </span>
                    <span className="mbmenu-row-label">{meta.label}</span>
                    <i className={`ti ti-chevron-${open ? 'down' : 'right'} mbmenu-chev`} />
                  </button>
                  {open && (
                    <div className="mbmenu-sub">
                      {items.map((i) => (
                        <button key={i.key} type="button" className="mbmenu-subrow" onClick={() => go(i.key)}>
                          <i className={`ti ${i.ico}`} /> {i.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Tiện ích */}
          {utilItems.length > 0 && (
            <>
              <div className="mbmenu-title">Tiện ích</div>
              <div className="mbmenu-sec">
                {utilItems.map((i) => (
                  <button key={i.key} type="button" className="mbmenu-row" onClick={() => go(i.key)}>
                    <span className="mbmenu-row-ico"><i className={`ti ${i.ico}`} /></span>
                    <span className="mbmenu-row-label">{i.label}</span>
                    <i className="ti ti-chevron-right mbmenu-chev" />
                  </button>
                ))}
              </div>
            </>
          )}

          {/* Giao diện */}
          <div className="mbmenu-title">Giao diện</div>
          <div className="mbmenu-sec mbmenu-appearance">
            <ThemeToggle />
            <AccentPicker />
          </div>

          {/* Đăng xuất */}
          <button type="button" className="mbmenu-row mbmenu-logout" onClick={logout}>
            <span className="mbmenu-row-ico"><i className="ti ti-logout" /></span>
            <span className="mbmenu-row-label">Đăng xuất</span>
          </button>

          <div className="small muted" style={{ textAlign: 'center', padding: '14px 0 6px' }}>
            FunnyTask · Phiên bản {APP_VERSION}
          </div>
        </>
      )}
    </div>
  );
}
